import React, { useMemo } from 'react'
import { useSelector } from 'APP/Hooks/redux'

// Services & Helpers
import { RootScreens } from 'APP/Lib/Enums'
import I18n from 'APP/I18n'
import {
  shouldRenderComponent,
  filterServiceGroupsAndServicesForEligibility,
} from 'APP/Lib/ContentHelpers'
import { selectExternalServices } from 'APP/Redux/PatientRedux'
import { contentSelectors } from 'APP/Store/Content'

// Containers
import TodayScreen from 'APP/Containers/TodayScreen'
import CmsAppTabScreen from 'APP/Containers/CmsAppTabScreen'

// Components
import { TabIcon } from 'APP/Nav/Components/TabIcon'

export const primaryScreens = [
  { name: RootScreens.today, component: TodayScreen, options: { headerShown: false } },
  { name: RootScreens.move, component: CmsAppTabScreen },
  { name: RootScreens.mind, component: CmsAppTabScreen },
  { name: RootScreens.chat, component: CmsAppTabScreen },
  { name: RootScreens.browse, component: CmsAppTabScreen },
]

export const withTabBarIcons = (tabs) =>
  tabs.map((tab) => ({
    ...tab,
    options: {
      ...tab?.options,
      tabBarIcon: (props) => (
        <TabIcon name={tab?.options?.tabBarIconName || tab?.name} {...props} />
      ),
    },
  }))

export const usePrimaryTabs = () => {
  const eligibleServices = useSelector((state) => state?.patient?.profile?.eligibleServices)
  const orderedEpisodes = useSelector((state) => state?.history?.orderedEpisodes)
  const carePlans = useSelector((state) => state?.history?.completedCarePlans)
  const cmsLayout = useSelector((state) => state.content?.layout?.data?.screens)
  const hasUnreadChannels = useSelector((state) => state.history?.hasUnreadChannels)
  const externalServices = useSelector(selectExternalServices)
  const allServiceGroups = useSelector(contentSelectors.selectAllServiceGroups)

  const tabs = useMemo(() => {
    if (!cmsLayout) return []

    // Merge the hardcode redesignNavItems and the CMS content, filtering based on any service filters sent by the CMS
    return cmsLayout?.reduce((normalizedNavItems, screen) => {
      const navItem = primaryScreens.find(({ name }) => screen.mobile_scene_key === name)

      let shouldRenderScreen = shouldRenderComponent(screen.service_filter, eligibleServices)

      // Only show the Services (chat) tab if at least one sub-tab has renderable content
      if (screen.mobile_scene_key === RootScreens.chat) {
        const hasGetCare =
          filterServiceGroupsAndServicesForEligibility(
            allServiceGroups,
            eligibleServices,
            externalServices
          ).length > 0
        const hasInbox = orderedEpisodes?.length > 0
        const hasCarePlans = carePlans?.length > 0
        shouldRenderScreen = hasGetCare || hasInbox || hasCarePlans
      }

      if (navItem && shouldRenderScreen) {
        const tabLabel = I18n.exists(`NavigationNameOverwrite.${navItem.name}`)
          ? I18n.t(`NavigationNameOverwrite.${navItem.name}`)
          : screen.title || navItem.name
        const tabBarIconName = I18n.exists(`NavigationIconNameOverwrite.${navItem.name}`)
          ? I18n.t(`NavigationIconNameOverwrite.${navItem.name}`)
          : screen.nav_icon_name
        const options = {
          ...navItem.options,
          title: tabLabel,
          tabBarIconName: tabBarIconName,
          tabBarLabel: tabLabel,
          tabBarBadge: (screen.mobile_scene_key === RootScreens.chat && hasUnreadChannels) || null,
        }
        normalizedNavItems.push({ ...navItem, options })
      }

      return normalizedNavItems
    }, [])
  }, [
    cmsLayout,
    eligibleServices,
    externalServices,
    allServiceGroups,
    orderedEpisodes,
    carePlans,
    hasUnreadChannels,
  ])

  return withTabBarIcons(tabs)
}

export const useSecondaryTabs = () => {
  const eligibleServices = useSelector((state) => state?.patient?.profile?.eligibleServices)
  const isWellBeingIndexAssessmentEligible = Boolean(eligibleServices?.wellbeing_index_assessment)
  const unreadCount = useSelector((state) => state.zendesk.unreadCount)
  return useMemo(() => {
    const tabs = [
      {
        name: 'account',
        options: {
          tabBarBadge: unreadCount > 0,
          tabBarLabel: I18n.t('AccountScreen.title'),
        },
      },
    ]

    if (isWellBeingIndexAssessmentEligible) {
      tabs.push({
        name: 'wellBeingIndex',
        options: {
          tabBarLabel: I18n.t('WellBeingIndexScreen.title'),
        },
      })
    }

    return withTabBarIcons(tabs)
  }, [isWellBeingIndexAssessmentEligible, unreadCount])
}
